/**
 * Utility for handling notification navigation
 */

/**
 * Determines where to navigate based on notification type and related data
 * @param {Object} notification - The notification object
 * @param {Function} navigate - React Router navigate function
 * @param {Object} user - Current user object
 * @returns {string|null} - The URL to navigate to, or null if no navigation
 */
export const getNotificationNavigationUrl = (notification, user) => {
  const { type, related_id, related_type } = notification;

  switch (type) {
    case 'wps_created':
    case 'wps_actual_hours_updated':
    case 'wps_monthly_summary':
    case 'admin_wps_monthly_summary':
      // Navigate to Work Planning page
      if (related_id && related_type === 'wps') {
        // For specific WPS entries, we could potentially filter to show that entry
        // For now, just navigate to the planning page
        return '/planning';
      }
      return '/planning';

    case 'wps_alert':
    case 'admin_wps_alert':
      // Legacy alerts - navigate to planning page
      return '/planning';

    case 'timesheet_created':
    case 'timesheet_updated':
      // Navigate to Time Entry page
      if (related_id && related_type === 'timesheet') {
        // Could include user_id as parameter if needed
        return '/time-logging';
      }
      return '/time-logging';

    case 'info':
    case 'success':
      // General notifications - no specific navigation
      return null;

    default:
      // Unknown notification type - no navigation
      return null;
  }
};

/**
 * Handles clicking on a notification
 * @param {Object} notification - The notification object
 * @param {Function} navigate - React Router navigate function
 * @param {Function} markAsRead - Function to mark notification as read
 * @param {Object} user - Current user object
 */
export const handleNotificationClick = async (notification, navigate, markAsRead, user) => {
  try {
    // Always mark as read when clicked
    if (!notification.read) {
      await markAsRead(notification.id);
    }

    // Get navigation URL
    const navigationUrl = getNotificationNavigationUrl(notification, user);
    
    if (navigationUrl) {
      // Add query parameters for better user experience
      const urlParams = new URLSearchParams();
      
      // Add user context if it's a user-specific notification
      if (notification.related_id && notification.related_type === 'wps') {
        // For WPS notifications, we might want to highlight or filter to specific entries
        urlParams.set('highlight_wps', notification.related_id);
        
        // For admin users, extract the user who performed the action from the message
        // to help them filter to that user's data
        if (user && (user.role === 'administrator' || user.role === 'admin')) {
          // Try to extract user info from notification message
          // Examples: "Werner Cube created a new WPS entry..." or "Werner Cube updated actual hours..."
          const message = notification.message;
          const userNameMatch = message.match(/^([^:]+?)(?:\s+(?:created|updated|added))/);
          if (userNameMatch) {
            const userName = userNameMatch[1].trim();
            urlParams.set('filter_user', userName);
          }
        }
      }
      
      // Navigate with parameters
      const finalUrl = urlParams.toString() ? `${navigationUrl}?${urlParams.toString()}` : navigationUrl;
      navigate(finalUrl);
    }
  } catch (error) {
    console.error('Error handling notification click:', error);
    // Still try to navigate even if marking as read fails
    const navigationUrl = getNotificationNavigationUrl(notification, user);
    if (navigationUrl) {
      navigate(navigationUrl);
    }
  }
};

/**
 * Gets a user-friendly description of where the notification will navigate
 * @param {Object} notification - The notification object
 * @returns {string} - Description of navigation action
 */
export const getNotificationActionDescription = (notification) => {
  const { type } = notification;

  switch (type) {
    case 'wps_created':
    case 'wps_actual_hours_updated':
    case 'wps_monthly_summary':
    case 'admin_wps_monthly_summary':
    case 'wps_alert':
    case 'admin_wps_alert':
      return 'Go to Work Planning';

    case 'timesheet_created':
    case 'timesheet_updated':
      return 'Go to Time Entry';

    case 'info':
    case 'success':
    default:
      return 'View details';
  }
}; 